<template>
  <article
    data-drag-handle
    class="relative w-full overflow-visible rounded-md border border-border-strong bg-bg-surface shadow-sm transition-[border-color,box-shadow,opacity,filter] duration-120 ease-standard hover:border-accent/70 hover:shadow-glow-accent select-none"
    :class="{ 'opacity-40 grayscale': isDimmedByTagFilter || dimmedByFilter }"
    :data-player-id="player.id"
  >
    <!--
      `data-drag-handle` on the <article> root makes the whole Detail-mode
      card a drag-initiating region for SortableJS (`handle:
      '[data-drag-handle]'` in `useDragAndDrop`). The card's interactive
      children (name fields, role badge, tag buttons, build chip, weapon
      cells) each carry `data-no-drag` so a click/press on them is never
      mis-read as a drag intent, while the surrounding card background
      (including the weapons column padding) can still start a drag.
      `PlayerCardCompact` uses the same root-handle pattern.
    -->
    <!-- Left + right path strips live on the <article> so they apply to both detail and compact modes. -->
    <div
      class="absolute top-0 bottom-0 left-0 w-1 pointer-events-none"
      :class="leftStripClass"
      aria-hidden="true"
    />
    <div
      class="absolute top-0 bottom-0 right-0 w-1 pointer-events-none"
      :class="rightStripClass"
      aria-hidden="true"
    />
    <template v-if="mode === ViewMode.Detail">
      <div class="relative flex flex-col items-stretch gap-1.5 px-2 py-1.5 sm:flex-row sm:items-center">
        <PlayerCardHeader
          :player="player"
          :mode="mode"
          :is-raid="props.boardMode === BoardMode.Raid"
          :can-edit="canEdit"
          :can-edit-tags="canManageTags"
          :left-path-color="leftPathColor"
          :right-path-color="rightPathColor"
          :left-strip-class="leftStripClass"
          :right-strip-class="rightStripClass"
          :assign-pending="assignPending"
          :assign-error="assignError"
          :can-manage-rsvp="canManageRsvp"
          :rsvp-pending="rsvpPending"
          :rsvp-error="rsvpError"
          @name-commit="(value) => store.renamePlayer(player.id, 'inGameName', value)"
          @tag-toggle="onTagToggle"
          @tag-assign="onTagAssign"
          @tag-unassign="onTagUnassign"
          @rsvp-toggle="onRsvpToggle"
        />
        <PlayerWeaponSlots
          :player="player"
          :mode="mode"
          :can-select-builds="canSelectBuilds"
          :can-manage-builds="canManageBuilds"
          :can-set-default-build="canSetDefaultBuild"
          :can-select-guild-builds="canSelectGuildBuilds"
          :can-use-unassigned-guild-builds="props.boardMode === BoardMode.Raid"
          :can-assign-guild-builds="canAssignGuildBuilds"
          :can-manage-guild-builds="canManageGuildBuilds"
          @use-build="(id, bi) => emit('use-build', id, bi)"
          @add-build="(id) => emit('add-build', id)"
          @use-guild-build="(id, buildId) => emit('use-guild-build', id, buildId)"
          @add-guild-build="emit('add-guild-build')"
          @edit-guild-build="(id, buildId, slot) => emit('edit-guild-build', id, buildId, slot)"
        />
      </div>
    </template>
    <PlayerCardCompact
      v-else
      :player="player"
      :is-raid="props.boardMode === BoardMode.Raid"
      :compact-nickname="compactNickname"
      :left-strip-class="leftStripClass"
      :right-strip-class="rightStripClass"
      :can-manage-rsvp="canManageRsvp"
      :rsvp-pending="rsvpPending"
      :rsvp-error="rsvpError"
      @rsvp-toggle="onRsvpToggle"
    />
  </article>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useViewTimeouts } from '@/shared/composables'
import { useRoleColors } from '@/features/gvg-planner/composables'
import { session } from '@/app/session'
import type { Weapon, WeaponRoleColor } from '@/types/weapon'
import { WEAPONS_BY_SLUG } from '@/data/weapons'
import type { Player } from '../../types'
import { BoardMode, ViewMode } from '../../types'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import { playerDisplayName } from '../../lib/playerIdentity'
import { resolveLivePlayerForRaidPlayer } from '../../lib/raidPlayerLiveLink'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import PlayerCardCompact from './PlayerCardCompact.vue'
import PlayerCardHeader from './PlayerCardHeader.vue'
import PlayerWeaponSlots from '../weapons/PlayerWeaponSlots.vue'

export interface PlayerCardProps {
  readonly player: Player
  /** Detail (default) renders the full card; Compact collapses to a single inline row. */
  readonly mode?: ViewMode
  /** `'live'` (default) lets mutations fire; `'raid'` gates every mutation through `canEdit`. */
  readonly boardMode?: BoardMode
  /** Board-wide highlight filters dim non-matches without removing them from DnD order. */
  readonly dimmedByFilter?: boolean
}

const props = withDefaults(defineProps<PlayerCardProps>(), {
  boardMode: BoardMode.Live,
  dimmedByFilter: false,
})

const emit = defineEmits<{
  (e: 'use-guild-build', playerId: string, buildId: string): void
  (e: 'add-guild-build'): void
  (e: 'edit-guild-build', playerId: string, buildId: string, slot: 0 | 1): void
  (e: 'use-build', playerId: string, buildIndex: number): void
  (e: 'add-build', playerId: string): void
}>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const store = useGvgPlannerStore()
const roleColors = useRoleColors()
const PATH_BG = roleColors.byColor

const mode = computed<ViewMode>(() => props.mode ?? ViewMode.Detail)
const dimmedByFilter = computed<boolean>(() => props.dimmedByFilter)

/** The board-wide identity preference applies equally to detail and compact cards. */
const compactNickname = computed<string>(() =>
  playerDisplayName(props.player, store.playerNameMode),
)
const isSessionPlayer = computed<boolean>(() => {
  const sessionDiscordUserId = session.value?.discordUserId
  return sessionDiscordUserId !== undefined
    && String(sessionDiscordUserId) === String(props.player.discordUserId)
})

/** Guild owner or officer + Live board mode gates live-player mutations on this card. */
const canEdit = computed<boolean>(
  () => (store.isOwner || store.isOfficer) && props.boardMode === BoardMode.Live,
)

/**
 * Guild-build assignment still targets the linked live player. Tag assignment
 * deliberately does not use this id: raid mode targets `raid_players.id` so
 * cloned plans can diverge.
 */
const guildBuildPlayerId = computed<string | null>(() => {
  if (store.activeRaidId === null) return props.player.id
  const raidPlayer = store.raidPlayers.find((player) => player.id === props.player.id)
  if (raidPlayer === undefined) return null
  return resolveLivePlayerForRaidPlayer(raidPlayer, store.players)?.id ?? null
})

/** Officers can edit live tags or the selected raid's snapshot tags. */
const canManageTags = computed<boolean>(
  () => (store.isOwner || store.isOfficer)
    && (props.boardMode === BoardMode.Live || !store.activeRaidEditLocked),
)

/**
 * The selector itself is visible on every pair of weapon cells so a card
 * never loses its build entry point when its equipped data is stale. The
 * mutation gate deliberately mirrors the resolvers:
 *
 * - live board: only the matching Discord identity can select a library build;
 * - raid snapshot: that identity or a guild officer/owner can select one.
 *
 * Opening a selector remains harmless for read-only viewers, but its rows are
 * rendered as non-interactive previews so the UI never advertises a mutation
 * the resolver would reject.
 */
const canSelectBuilds = computed<boolean>(() => {
  if (props.boardMode === BoardMode.Live) return isSessionPlayer.value
  if (store.activeRaidEditLocked) return false
  return isSessionPlayer.value || store.isOwner || store.isOfficer
})

/** Saved-build writes are owned by the matching Discord identity, never guild role. */
const canManageBuilds = computed<boolean>(() =>
  props.boardMode === BoardMode.Live
    && isSessionPlayer.value,
)
const canSetDefaultBuild = computed<boolean>(
  () => isSessionPlayer.value,
)
const canSelectGuildBuilds = computed<boolean>(() => {
  if (props.boardMode === BoardMode.Raid && store.activeRaidEditLocked) return false
  return isSessionPlayer.value || store.isOwner || store.isOfficer
})

/** Only the matching signed-in identity can change its own raid RSVP. */
const canManageRsvp = computed<boolean>(() =>
  props.boardMode === BoardMode.Raid
    && store.activeRaidId !== null
    && !store.activeRaidEditLocked
    && isSessionPlayer.value,
)

const canManageGuildBuilds = computed<boolean>(() =>
  props.boardMode === BoardMode.Live && (store.isOwner || store.isOfficer),
)
const canAssignGuildBuilds = computed<boolean>(() =>
  (store.isOwner || store.isOfficer) && guildBuildPlayerId.value !== null,
)


function weaponAtDisplay(displayIdx: 0 | 1): Weapon | undefined {
  const w = props.player.mainWeapons
  if (displayIdx >= w.length) return undefined
  const slug = w[displayIdx]
  return slug ? WEAPONS_BY_SLUG.get(slug) : undefined
}

const leftPathColor = computed<WeaponRoleColor | null>(() => weaponAtDisplay(0)?.color ?? null)
const rightPathColor = computed<WeaponRoleColor | null>(() => {
  const second = weaponAtDisplay(1)
  if (second) return second.color
  const first = weaponAtDisplay(0)
  return first ? first.color : null
})

const leftStripClass = computed<string>(() =>
  leftPathColor.value ? PATH_BG[leftPathColor.value].bg : 'bg-bg-surface',
)
const rightStripClass = computed<string>(() =>
  rightPathColor.value ? PATH_BG[rightPathColor.value].bg : 'bg-bg-surface',
)

function onTagToggle(tagId: string): void {
  if (store.isActiveTagFilter(tagId)) {
    store.setActiveTagFilter(null)
    return
  }
  store.setActiveTagFilter(tagId)
}

/**
 * Local pending + error flags for the awaited `store.assignTag`
 * Promise. We don't reach into the store's `error.value` for the
 * inline surface because that ref is shared across every mutation on
 * the board (room edits, weapon picks, etc.) — pinning a per-card
 * error here keeps the chip-strip failure visible without being
 * clobbered by an unrelated mutation landing on top.
 *
 * `assignPending` is also exposed via the `assignPending` prop chain
 * (card → header → strip) so the strip can grey out its options and
 * the `+ Tag` trigger while the dispatch is in flight, preventing
 * double submits inside the request window.
 */
const assignPending = ref<boolean>(false)
const assignError = ref<string | null>(null)
const rsvpPending = ref<boolean>(false)
const rsvpError = ref<string | null>(null)
const rsvpTarget = ref<boolean | null>(null)
const { scheduleTimeout, clearPendingTimeout } = useViewTimeouts()
const RSVP_SYNC_TIMEOUT_MS = 10_000

function finishRsvpSync(): void {
  clearPendingTimeout()
  rsvpPending.value = false
  rsvpError.value = null
  rsvpTarget.value = null
}

watch(
  () => props.player.hasSignedIn,
  (hasSignedIn) => {
    if (rsvpTarget.value === hasSignedIn) finishRsvpSync()
  },
)

async function onRsvpToggle(): Promise<void> {
  if (!canManageRsvp.value || rsvpPending.value) return
  const target = !props.player.hasSignedIn
  rsvpTarget.value = target
  rsvpPending.value = true
  rsvpError.value = null
  const acknowledged = await store.setRaidRsvp(target)
  if (!acknowledged) {
    rsvpPending.value = false
    rsvpTarget.value = null
    rsvpError.value = store.error ?? messages.value.player.rsvpError
    return
  }
  if (props.player.hasSignedIn === target) {
    finishRsvpSync()
    return
  }
  scheduleTimeout(() => {
    rsvpPending.value = false
    rsvpError.value = messages.value.player.rsvpSyncTimeout
  }, RSVP_SYNC_TIMEOUT_MS)
}

async function onTagAssign(tagId: string): Promise<void> {
  if (!canManageTags.value || assignPending.value) return
  assignPending.value = true
  assignError.value = null
  try {
    const ok = await store.assignTag(props.player.id, tagId)
    if (!ok) {
      assignError.value = store.error ?? messages.value.player.assignTagError
    }
  } catch (e: unknown) {
    // Defence in depth — the store's runReporting runner is supposed
    // to swallow errors into `error.value`, but a future runner
    // refactor must not be able to surface an unhandled rejection here.
    assignError.value = e instanceof Error ? e.message : String(e)
  } finally {
    assignPending.value = false
  }
}

function onTagUnassign(tagId: string): void {
  if (!canManageTags.value) return
  void store.unassignTag(props.player.id, tagId)
}

/** Plan 05 — dim non-matching players when a tag filter is active (keeps DnD ordering intact). */
const isDimmedByTagFilter = computed<boolean>(() => {
  const active = store.activeTagFilter
  if (active === null) return false
  return !props.player.tags.some((t) => t.id === active)
})
</script>
